// Sidestep dodge (owner 2026-11-02: 'when an enemy misses, we hear, maybe see the character do … a dodge, or maybe
// teleports'). Render-only reaction to the sim's 'dodge' event: Aren blinks ~1.1 m to one side and slides back, leaving
// a fading spectral afterimage where the hit landed, with a streak of motes between the two. The sim position, hitbox
// and i-frames are unchanged — this only offsets where the hero is DRAWN for ~0.45 s.
import * as THREE from 'three/webgpu';
import type { Fx } from './fx';
import { GltfHero } from './heroGltf';
import type { RenderStyle } from './toon';

const OUT = 0.06, HOLD = 0.16, BACK = 0.45, DIST = 1.1, GHOST_LIFE = 0.55;

export class DodgeFx {
  private t = 9; private dx = 0; private dz = 0;
  private ghost: GltfHero | null = null; private ghostLoading = false; private ghostT = 9; private ghostYaw = 0;
  /** current draw offset for the hero (world units) */
  readonly off = new THREE.Vector2();

  constructor(private scene: THREE.Scene, private style: RenderStyle, private file: () => string) {
    setTimeout(() => this.load(), 4000); // preload so the very first dodge already leaves an afterimage
  }
  /** LOAD-PERF: the loading-screen warm-up builds the afterimage up front (its shader compiles there, not at the first dodge) */
  preload(): void { this.load(); }
  private load(): void {
    if (this.ghost || this.ghostLoading) return;
    this.ghostLoading = true;
    const g = new GltfHero(this.style);
    void g.load(`${import.meta.env.BASE_URL}models/${this.file()}`).then(() => { g.makeGhost(); g.root.visible = false; this.scene.add(g.root); this.ghost = g; }).catch(() => { this.ghostLoading = false; });
  }

  /** a dodge happened: hero at (hx, gy, hz) facing yaw */
  trigger(hx: number, gy: number, hz: number, yaw: number, fx: Fx): void {
    this.load();
    // sidestep perpendicular to the facing, random side
    const side = Math.random() < 0.5 ? -1 : 1, fxv = Math.sin(yaw), fzv = Math.cos(yaw);
    this.dx = fzv * side; this.dz = -fxv * side; this.t = 0;
    // afterimage stays where the blow landed
    this.ghostT = 0; this.ghostYaw = yaw;
    if (this.ghost) { this.ghost.root.position.set(hx, gy, hz); this.ghost.root.rotation.y = yaw; }
    // streak of motes from the old spot to the new one + a small kick of dust at both feet positions
    const ex = hx + this.dx * DIST, ez = hz + this.dz * DIST;
    for (let k = 0; k < 14; k++) {
      const u = k / 13, y = gy + 0.4 + Math.random() * 1.4;
      fx.particles.emitDir(hx + (ex - hx) * u, y, hz + (ez - hz) * u, this.dx * 3 + (Math.random() - 0.5), 0.6, this.dz * 3 + (Math.random() - 0.5), k % 3 ? 0x9fe8ff : 0xffffff, 0.22, 0.3, 2.4);
    }
    for (const [x, z] of [[hx, hz], [ex, ez]]) for (let k = 0; k < 5; k++) {
      const a = Math.random() * Math.PI * 2; fx.particles.emitDir(x, gy + 0.1, z, Math.cos(a) * 2, 0.8 + Math.random(), Math.sin(a) * 2, 0xc8b490, 0.35, 0.4, 0.9);
    }
    fx.rings.add(hx, gy + 0.1, hz, 1.4, 0.22, 0x9fe8ff, 2, 1);
  }

  update(dt: number, gy: number): void {
    this.t += dt;
    let k = 0;
    if (this.t < OUT) k = this.t / OUT;
    else if (this.t < HOLD) k = 1;
    else if (this.t < BACK) { const u = (this.t - HOLD) / (BACK - HOLD); k = 1 - u * u * (3 - 2 * u); }
    this.off.set(this.dx * DIST * k, this.dz * DIST * k);
    if (this.ghost) {
      this.ghostT += dt;
      const a = this.ghostT < GHOST_LIFE ? (1 - this.ghostT / GHOST_LIFE) ** 0.7 : 0;
      this.ghost.root.visible = a > 0.01;
      if (a > 0.01) {
        this.ghost.ghostAlpha.value = a; this.ghost.root.position.y = gy;
        this.ghost.update(dt, { speed: 0, firing: false, charging: false, transformed: false, surgeTier: 0.9, hurt: false, dead: false, faceYaw: this.ghostYaw, moveYaw: this.ghostYaw, turn: 0 });
      }
    }
  }
  clear(): void { this.t = 9; this.ghostT = 9; this.off.set(0, 0); if (this.ghost) this.ghost.root.visible = false; }
}
